import React, {useEffect, useMemo, useState} from 'react';
import {GoogleMap, MarkerF, PolylineF} from "@react-google-maps/api";
import useLocation from "../../Hooks/useLocation";
import ConditionalRender from "../ConditionalRender";
import redDot from 'assets/red-dot.svg'
import greenDot from 'assets/green-dot.svg'
import orangeDot from 'assets/orange-dot.svg'
import yellowDot from 'assets/yellow-dot.svg'
import blueDot from 'assets/blue-dot.svg'
import {formatISODate} from "../../utils/DateTimeUtills";
import GeofencePolygon from "./GeofencePolygon";
import useTooltip from "../../Hooks/visual/useTooltip";

const MsisdnLocationMap = ({ containerStyle, geofences, locations, selectedLocation, onLocationClick }) => {

    const { userLocation, isLoading } = useLocation({ lat: 51.441277, lng: 5.476341 }) //Eindhoven
    const {showTooltip, hideTooltip} = useTooltip(10, 10, {zIndex: 1105})

    const center = useMemo(() => {
        return userLocation;
    }, [userLocation])

    const [mapInstance, setMapInstance] = useState(null);

    const handleMapLoad = (map) => {
        // Store map instance when it's loaded
        setMapInstance(map);
    };

    useEffect(() => {
        if (mapInstance && !isLoading) {
            if(locations.length > 0)
            {
                const bounds = new window.google.maps.LatLngBounds();
                locations.forEach(location => {
                    bounds.extend(new window.google.maps.LatLng(location.latitude, location.longitude));
                });

                mapInstance.fitBounds(bounds);
            }
        }
    }, [mapInstance, locations]);

    const handleMouseOver = (location) => {
        return (e) =>
        {
            const time = formatISODate(location.timestamp, {format: 'timeOnly', useLocalTime: true});
            showTooltip(time, e.domEvent.clientX, e.domEvent.clientY);
        }
    }

    const handleMouseOut = () => {
        hideTooltip();
    }


    const geofenceOptions = {
        strokeColor: "#3e79cc",
        strokeOpacity: 0.8,
        strokeWeight: 3,
        fillColor: "#6ca1e8",
        fillOpacity: 0.5,
        zIndex: 1
    }

    // Define polyline options for connecting locations
    const polylineOptions = {
        strokeColor: "#de4d4d",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        zIndex: 2
    };

    return (
        <ConditionalRender condition={!isLoading}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                onLoad={handleMapLoad}
                center={center}
                zoom={12}
                options={{
                    disableDefaultUI: true,
                    zoomControl: true,
                    clickableIcons: false,
                    mapTypeControl: true,
                    styles: [
                        {
                            featureType: 'poi',
                            elementType: 'labels',
                            stylers: [
                                {
                                    visibility: 'off',
                                },
                            ]
                        },
                        {
                            featureType: 'transit.station',
                            stylers: [
                                {
                                    visibility: 'off',
                                },
                            ],
                        },
                    ]
                }}>
                {geofences && geofences.map((geofence, index) => <GeofencePolygon
                    key={index}
                    geofence={geofence}
                    geofenceOptions={geofenceOptions}/>
                )}

                {locations && locations.map((location, index) => <React.Fragment key={index}>
                        <MarkerF position={{lat: location.latitude, lng: location.longitude}}
                                 icon={{
                                    url: (() => {
                                      if (selectedLocation === location) {
                                        return blueDot;
                                      }
                                      if (location.activityType === 'Walking') {
                                        return greenDot; 
                                      }
                                      if (location.activityType === 'Driving') {
                                        return yellowDot; 
                                      }
                                      if (location.activityType === 'Unknown'){
                                        return orangeDot
                                      }
                                      return redDot; 
                                    })(),
                                     strokeWeight: 20,
                                     anchor: new window.google.maps.Point(8, 8),
                                     scaledSize: selectedLocation && selectedLocation === location ? new window.google.maps.Size(28, 28) : null, // Scaled size
                                  }}
                                 onClick={() => onLocationClick(index)}
                                 onMouseOver={handleMouseOver(location)}
                                 onMouseOut={handleMouseOut}
                                 options={{
                                     opacity: selectedLocation && selectedLocation !== location ? 0.7 : 1,
                                     zIndex: selectedLocation && selectedLocation !== location ? 1 : 10,
                                 }}>
                        </MarkerF>

                        {index < locations.length - 1 && <PolylineF
                            path={[
                                { lat: location.latitude, lng: location.longitude },
                                { lat: locations[index + 1].latitude, lng: locations[index + 1].longitude }
                            ]}
                            options={polylineOptions}
                        />}
                </React.Fragment>
                )};
            </GoogleMap>
        </ConditionalRender>
    );
};

MsisdnLocationMap.defaultProps = {
    geofences: [],
    locations: [],
    containerStyle: {
        positions: 'relative',
        width: '100%',
        height: '100%',
        flexGrow: 1
    }
}

export default MsisdnLocationMap;